import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import type { TBMAttendee } from '../../lib/tbm';

interface QRModalProps {
  signUrl: string;
  isOpen: boolean;
  onClose: () => void;
  /** 실시간 서명 현황 */
  attendees?: TBMAttendee[];
}

export default function QRModal({ signUrl, isOpen, onClose, attendees }: QRModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && canvasRef.current && signUrl) {
      QRCode.toCanvas(canvasRef.current, signUrl, {
        width: 280,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
    }
  }, [isOpen, signUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // 새 서명 추가 시 하단 자동 스크롤
  const signedCount = attendees?.filter(a => a.signedAt).length || 0;
  useEffect(() => {
    if (isOpen && listEndRef.current) {
      listEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [signedCount, isOpen]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(signUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  const totalCount = attendees?.length || 0;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-white flex flex-col">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-ink-900">TBM 서명 QR</h3>
          {totalCount > 0 && (
            <span className="text-sm font-medium text-brand-600">
              {signedCount}/{totalCount}명
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-ink-400 hover:text-ink-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* QR 코드 + 서명링크 */}
      <div className="flex flex-col items-center px-4 pt-4 pb-2">
        <p className="text-sm text-ink-500 mb-3">
          작업자에게 QR 코드를 보여주세요
        </p>
        {/* eslint-disable-next-line local/no-self-painted-card -- Card 갈래 아님: QR canvas를 감싼 미디어 프레임(스캔 대비용 흰 여백이라 카드 여백 tier와 무관) */}
        <div className="bg-white p-3 rounded-2xl shadow-card border border-line-soft">
          <canvas ref={canvasRef} />
        </div>
        {/* 서명 링크 */}
        <div className="w-full mt-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={signUrl}
              className="flex-1 px-3 py-2 border border-line rounded-control bg-surface-soft text-sm text-ink-700 truncate"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 px-3 py-2 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-card-sm hover:bg-brand-100 transition-colors"
            >
              {copied ? '복사됨' : '복사'}
            </button>
          </div>
        </div>
      </div>

      {/* 실시간 서명 현황 */}
      {attendees && attendees.length > 0 && (
        <div className="flex-1 flex flex-col min-h-0 border-t border-line mt-2">
          <div className="px-4 py-2 bg-surface-soft">
            <h4 className="text-sm font-semibold text-ink-700">
              서명 현황 <span className="text-brand-600">{signedCount}/{totalCount}</span>
            </h4>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            <div className="divide-y divide-line-soft">
              {attendees.map((a, idx) => (
                <div key={a.id} className="flex items-center gap-3 py-2">
                  <span className="text-xs text-ink-400 w-6 text-right">{idx + 1}</span>
                  <span className="flex-1 text-sm font-medium text-ink-900 truncate">{a.name}</span>
                  {a.signedAt ? (
                    a.signatureData ? (
                      <img src={a.signatureData} alt={`${a.name} 서명`} className="h-5 w-auto" />
                    ) : (
                      <span className="text-xs text-green-600 font-medium shrink-0">완료</span>
                    )
                  ) : (
                    <span className="text-xs text-ink-300 shrink-0">대기</span>
                  )}
                </div>
              ))}
            </div>
            <div ref={listEndRef} />
          </div>
        </div>
      )}

      {/* 하단 닫기 */}
      <div className="px-4 py-3 border-t border-line">
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2.5 text-sm font-medium text-ink-700 bg-ink-100 rounded-card hover:bg-ink-200 transition-colors"
        >
          닫기
        </button>
      </div>
    </div>,
    document.body,
  );
}
